import { Fragment, useState, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { CaretDown, CaretUp, BellSimple } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@/helpers/hooks/useSnackbar'
import { getApiErrorMessage } from '@/utils/error'

import { cutTooLong } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import PicturesModal from '../../../Modal/PicturesModal'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Filter from '../../../Filter/Filter'
import {
  ScheduledDateField,
  ScheduledTimeField,
} from '../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import {
  formatSuggestedTimeframe,
  applyScheduledDateTimeChange,
  fromScheduledTimestamp,
  getScheduledDateTimeValidity,
  toScheduledTimestamp,
} from '../../ScheduledDateTimeFields/helpers'
import {
  maintenanceRequestTypes,
  maintenanceRealPageLocationTypes,
  maintenanceRequestStatusTypes,
  booleanList,
  parseToSelect,
  parseLocationToSelect,
  parsePriorityToSelect,
} from '../../MaintenanceRequests/helpers'

import MaintenanceRequestResmanRequestSummary from './MaintenanceRequestResmanRequestSummary'
import NotifyModal from './NotifyModal'
import { getCustomMaintenanceCategories } from '../../maintenance-categories'

const STRING_LENGTH = 350

const MaintenanceRequestDetailsBody = ({
  data,
  notes,
  jobSummaryMessage,
  setStatus,
  status,
  setShowNotesModal,
  setShowJobSummaryModal,
  setShowCompleteRequestModal,
  description,
  setShowDescriptionModal,
  entryNote,
  setShowEntryNoteModal,
  staffList,
  yardiConfig,
  rentManagerConfig,
  setShowAddNoteModal,
  resmanConfig,
  realPageConfig,
  updateMaintenanceRequestValue,
  errorMessage,
  isEditMode,
  isLoading,
  buildingData: {
    id: buildingId,
    realPagePropertyId,
    yardiPropertyId,
    timezone,
    resmanPropertyId,
    rentManagerPropertyId,
  },
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const { displaySnackbar } = useSnackbar()
  const entrataPropertyId = false // TODO: move it to buildingData after implementing an entrata
  const [shortDescription, setShortDescription] = useState(data.shortDescription || '')
  const [isPicturesModalVisible, setPicturesModalVisible] = useState(false)
  const [isNotifyModalVisible, setNotifyModalVisible] = useState(false)
  const [photosToShow, setPhotosToShow] = useState([])
  const isDisabled = ['Finished', 'Canceled'].includes(status && status.text ? status.text : '')
  const [issue, setIssue] = useState(data.issue || '')
  const [priority, setPriority] = useState()
  const [assignee, setAssignee] = useState()
  const [scheduledDateTime, setScheduledDateTime] = useState(() =>
    fromScheduledTimestamp(data.scheduledDate, timezone),
  )
  const [pets, setPets] = useState(parseToSelect(booleanList).find((pet) => pet.id === data.pets))
  const [realPageIssue, setRealPageIssue] = useState()
  const [category, setCategory] = useState()
  const [subcategory, setSubcategory] = useState()
  const [customIssue, setCustomIssue] = useState(false)
  const [subcategoriesToSelect, setSubcategoriesToSelect] = useState([])
  const [showRequestDetails, setShowRequestDetails] = useState(true)
  const [showNotes, setShowNotes] = useState(true)
  const scheduledDateValidity = getScheduledDateTimeValidity(
    scheduledDateTime.date,
    scheduledDateTime.time,
  )

  const updateSubcategory = (category) => {
    if (yardiConfig && yardiConfig.categories && category) {
      const selectedCategory = yardiConfig.categories.find(
        (yardiCategory) => String(yardiCategory.Name) === category.text,
      )
      if (selectedCategory && selectedCategory.SubCategory) {
        const subcategoriesToSelect = parseToSelect(selectedCategory.SubCategory)
        setSubcategoriesToSelect(subcategoriesToSelect)
        const subcategory = subcategoriesToSelect.find(
          (subcategory) => subcategory.text === data.subcategory,
        )
        subcategory && setSubcategory(subcategory)
      }
    }
  }

  const categories = useMemo(() => {
    return yardiConfig && yardiConfig.categories
      ? yardiConfig.categories.map((category) => category.Name)
      : rentManagerConfig && rentManagerConfig.categories
        ? rentManagerConfig.categories.map((category) => category.name)
        : (getCustomMaintenanceCategories(buildingId) ?? maintenanceRequestTypes)
  }, [yardiConfig, rentManagerConfig, buildingId])

  const categoriesToSelect = useMemo(() => parseToSelect(categories), [categories])

  const realPageIssuesToSelect = useMemo(() => {
    return realPageConfig && realPageConfig.issues.data
      ? realPageConfig.issues.data.map((issue) => ({
          id: issue.psID,
          text: issue.psDescription,
          value: issue.psDescription,
        }))
      : []
  }, [realPageConfig])

  const [enterPermission, setEnterPermission] = useState(
    parseToSelect(booleanList).find(
      (enterPermission) => enterPermission.id === data.enterPermission,
    ),
  )
  const [location, setLocation] = useState()

  const locationsToSelect = useMemo(() => {
    return realPageConfig?.locations?.data?.length
      ? parseLocationToSelect(realPageConfig.locations.data)
      : maintenanceRealPageLocationTypes
  }, [realPageConfig])

  const priorities = useMemo(() => {
    return yardiConfig && yardiConfig.priorities
      ? yardiConfig.priorities
      : rentManagerConfig && rentManagerConfig.priorities
        ? rentManagerConfig.priorities.map((priority) => priority.name)
        : []
  }, [yardiConfig, rentManagerConfig])

  const prioritiesToSelect = useMemo(() => parsePriorityToSelect(priorities), [priorities])

  const isClassicBuilding = !(
    realPagePropertyId ||
    entrataPropertyId ||
    yardiPropertyId ||
    resmanPropertyId
  )

  const displayFilters =
    isClassicBuilding || !!entrataPropertyId || !!yardiPropertyId || !!rentManagerPropertyId

  const updateTextValue = useMemo(() => {
    return debounce(updateMaintenanceRequestValue, 800)
  }, [])

  useEffect(() => {
    const category = categoriesToSelect.find((category) => category.text === data.category)
    if (category) {
      setCategory(category)
      updateSubcategory(category)
    }
  }, [categoriesToSelect, data.category])

  useEffect(() => {
    const realPageIssue = realPageIssuesToSelect.find((issue) => issue.text === data.issue)
    if (realPageIssue) {
      setRealPageIssue(realPageIssue)
    } else if (!realPageIssue && data.issue) {
      setCustomIssue(true)
    }
  }, [realPageIssuesToSelect, data.issue])

  useEffect(() => {
    setLocation(locationsToSelect.find((location) => location.text === data.location))
  }, [locationsToSelect, data.location])

  useEffect(() => {
    const priority = prioritiesToSelect.find((priority) => priority.text === data.priority)
    if (priority) {
      setPriority(priority)
    }
  }, [prioritiesToSelect, data.priority])

  useEffect(() => {
    if (staffList.length) {
      setAssignee(staffList.find((assignee) => data.assignee && assignee.id === data.assignee.id))
    }
  }, [staffList])

  useEffect(() => {
    setScheduledDateTime(fromScheduledTimestamp(data.scheduledDate, timezone))
  }, [data.scheduledDate, timezone])

  useEffect(() => {
    return () => {
      updateTextValue.cancel()
    }
  }, [updateTextValue])

  const renderPhotos = (photos) => (
    <div onClick={() => handleOpenModalWithPhotos(photos)} className="photos-list">
      {photos[0] && (
        <img src={getAssetLink({ path: 'chitown-building', file: photos[0] })} alt="" />
      )}
      {photos[1] && (
        <img src={getAssetLink({ path: 'chitown-building', file: photos[1] })} alt="" />
      )}
      {photos[2] && (
        <img src={getAssetLink({ path: 'chitown-building', file: photos[2] })} alt="" />
      )}
    </div>
  )

  const updateMaintenanceRequestAssignee = async (staff) => {
    // Only the assignee (and the status reset) is sent: scheduledDate / startRepairDate /
    // beforeRepairPhotos must stay untouched, selecting one field must not clear the others.
    const dataToUpdate = {
      status: 'Pending',
    }

    await updateMaintenanceRequestValue(
      {
        assignee: staff.id,
        ...dataToUpdate,
      },
      {
        ...dataToUpdate,
        assignee: {
          id: staff.id,
          user: {
            firstName: staff.user.firstName,
            lastName: staff.user.lastName,
            avatar: staff.user.avatar,
            id: Number(staff.user.id),
          },
        },
      },
    )
    setAssignee(staff)
    setStatus(
      parseToSelect(maintenanceRequestStatusTypes).find(
        (status) => status.text === dataToUpdate.status,
      ),
    )
  }

  // Note: the day + time slot are picked in the building's timezone (same as the creation
  // form). Only a complete pair (or no date at all) is saved; a half-filled pair shows an error.
  const handleScheduledDateTimeChange = (changes) => {
    const next = applyScheduledDateTimeChange(scheduledDateTime, changes, timezone)
    setScheduledDateTime(next)

    const { isDateMissing, isTimeMissing } = getScheduledDateTimeValidity(next.date, next.time)
    if (isDateMissing || isTimeMissing) return

    updateMaintenanceRequestValue({
      scheduledDate: toScheduledTimestamp(next.date, next.time, timezone),
    })
  }

  const handleOpenModalWithPhotos = (photos) => {
    if (photos.length) {
      setPhotosToShow(photos)
      setPicturesModalVisible(true)
    }
  }

  const onCategoryChange = (e) => {
    updateMaintenanceRequestValue({ category: e.target.value.text })
    setCategory(e.target.value)
    if (yardiConfig) {
      updateSubcategory(e.target.value)
      setSubcategory(undefined)
    }
  }

  const renderRequestSummary = () => {
    if (resmanConfig && resmanPropertyId) {
      return (
        <MaintenanceRequestResmanRequestSummary
          data={data}
          description={description}
          resmanConfig={resmanConfig}
          resmanPropertyId={resmanPropertyId}
          isEditMode={isEditMode}
          isDisabled={isDisabled}
          updateMaintenanceRequest={updateMaintenanceRequestValue}
          setShowDescriptionModal={setShowDescriptionModal}
        />
      )
    }

    return (
      <div className="details-content-block">
        <h4 className="details-content-title details-content-block-title">
          {t('maintenance:requestSummary.title')}
        </h4>
        <div className="details-content-container">
          {yardiPropertyId && (
            <div className="details-content-section">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.briefDescription.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <div className="field-wrapper">
                    <TextField
                      name="shortDescription"
                      placeholder={t('maintenance:requestSummary.briefDescription.placeholder')}
                      value={shortDescription}
                      onChange={(e) => {
                        setShortDescription(e.target.value)
                        updateTextValue({ shortDescription: e.target.value })
                      }}
                      disabled={isDisabled}
                      maxLength="255"
                    />
                  </div>
                ) : (
                  data.shortDescription || '-'
                )}
              </p>
            </div>
          )}
          {!!description && (
            <div className="details-content-section">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.fullDescription.title')}
              </h4>
              <p className="details-content-description">
                {cutTooLong(description, STRING_LENGTH)}
              </p>
              <button onClick={() => setShowDescriptionModal(true)} className="text-button">
                {t('maintenance:requestSummary.seeMore')}
              </button>
            </div>
          )}
          {entryNote && (
            <div className="details-content-section">
              <h4 className="details-content-title">
                {t('maintenance:requestPreview.details.entryNote.title')}
              </h4>
              <p className="details-content-description">{cutTooLong(entryNote, STRING_LENGTH)}</p>
              <button onClick={() => setShowEntryNoteModal(true)} className="text-button">
                {t('maintenance:requestSummary.seeMore')}
              </button>
            </div>
          )}
          {displayFilters && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.category.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <Filter
                    placeholder={
                      category
                        ? category.text
                        : t('maintenance:requestSummary.category.placeholder')
                    }
                    options={categoriesToSelect}
                    value={category ? [category] : []}
                    name="Category"
                    disabled={isDisabled}
                    closeAfterSelect
                    variant="rounded"
                    additionalClass={'details-filter'}
                    onChange={onCategoryChange}
                  />
                ) : (
                  data.category || t('maintenance:requestSummary.category.fallback')
                )}
              </p>
            </div>
          )}
          {yardiPropertyId && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.subcategory.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <Filter
                    placeholder={
                      subcategory
                        ? subcategory.text
                        : t('maintenance:requestSummary.subcategory.placeholder')
                    }
                    options={subcategoriesToSelect}
                    value={subcategory ? [subcategory] : []}
                    name="Subcategory"
                    variant="rounded"
                    disabled={isDisabled}
                    closeAfterSelect
                    additionalClass={'details-filter'}
                    onChange={(e) => {
                      updateMaintenanceRequestValue({ subcategory: e.target.value.text })
                      setSubcategory(e.target.value)
                    }}
                  />
                ) : (
                  data.subcategory || t('maintenance:requestSummary.subcategory.fallback')
                )}
              </p>
            </div>
          )}
          <div className="details-content-section details-content-section-row">
            <h4 className="details-content-title">
              {t('maintenance:requestSummary.permissionToEnter.title')}
            </h4>
            <p className="details-content-description">
              {isEditMode ? (
                <Filter
                  placeholder={
                    enterPermission
                      ? enterPermission.text
                      : t('maintenance:requestSummary.permissionToEnter.placeholder')
                  }
                  options={parseToSelect(booleanList)}
                  value={enterPermission ? [enterPermission] : []}
                  name="Permission to Enter"
                  variant="rounded"
                  disabled={isDisabled}
                  closeAfterSelect
                  additionalClass={'details-filter'}
                  onChange={(e) => {
                    updateMaintenanceRequestValue({ enterPermission: e.target.value.id })
                    setEnterPermission(e.target.value)
                  }}
                />
              ) : data.enterPermission ? (
                t('common:booleans.yes')
              ) : (
                t('common:booleans.no')
              )}
            </p>
          </div>
          {displayFilters && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.pets.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <Filter
                    placeholder={
                      pets ? pets.text : t('maintenance:requestSummary.pets.placeholder')
                    }
                    options={parseToSelect(booleanList)}
                    value={pets ? [pets] : []}
                    name="Pets"
                    disabled={isDisabled}
                    closeAfterSelect
                    variant="rounded"
                    additionalClass={'details-filter'}
                    onChange={(e) => {
                      updateMaintenanceRequestValue({ pets: e.target.value.id })
                      setPets(e.target.value)
                    }}
                  />
                ) : data.pets ? (
                  t('common:booleans.yes')
                ) : (
                  t('common:booleans.no')
                )}
              </p>
            </div>
          )}
          <div className="details-content-section details-content-section-row">
            <h4 className="details-content-title">
              {t('maintenance:requestSummary.status.title')}
            </h4>
            <p className="details-content-description">
              {isEditMode ? (
                <Filter
                  placeholder={
                    status ? status.text : t('maintenance:requestSummary.status.placeholder')
                  }
                  options={parseToSelect(maintenanceRequestStatusTypes)}
                  value={status ? [status] : []}
                  name="Status"
                  closeAfterSelect
                  disabled={isDisabled}
                  variant="rounded"
                  additionalClass={'details-filter'}
                  onChange={async (e) => {
                    const nextStatus = e.target.value

                    // Finished requires a job summary, same as the "Complete request" button
                    if (nextStatus.text === 'Finished') {
                      setShowCompleteRequestModal(true)
                      return
                    }

                    let hasFailed = false
                    await updateMaintenanceRequestValue(
                      { status: nextStatus.text },
                      undefined,
                      (err) => {
                        hasFailed = true
                        displaySnackbar(getApiErrorMessage(err?.response?.data), true)
                      },
                    )
                    if (!hasFailed) setStatus(nextStatus)
                  }}
                />
              ) : (
                data.status
              )}
            </p>
          </div>
          {realPagePropertyId && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.location.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <Filter
                    placeholder={
                      location && location.text
                        ? location.text
                        : t('maintenance:requestSummary.location.placeholder')
                    }
                    options={locationsToSelect}
                    value={location ? [location] : []}
                    name="Location"
                    closeAfterSelect
                    disabled={isDisabled}
                    variant="rounded"
                    additionalClass={'details-filter'}
                    onChange={(e) => {
                      updateMaintenanceRequestValue({ location: e.target.value.text })
                      setLocation(e.target.value)
                    }}
                  />
                ) : (
                  data.location
                )}
              </p>
            </div>
          )}
          {data.photos && data.photos.length ? (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.residentPhotos')}
              </h4>
              <p className="details-content-description">
                <div
                  onClick={() => handleOpenModalWithPhotos(data.photos)}
                  className="resident-photo"
                >
                  <img
                    className="resident-photo__img"
                    src={getAssetLink({ path: 'chitown-building', file: data.photos[0] })}
                    alt=""
                  />
                </div>
              </p>
            </div>
          ) : null}
          {realPagePropertyId && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.issue.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <Fragment>
                    <div className="input-column-container__header">
                      <button
                        onClick={() => setCustomIssue((prevState) => !prevState)}
                        className="text-button"
                      >
                        {customIssue
                          ? t('maintenance:requestSummary.issue.selectIssue')
                          : t('maintenance:requestSummary.issue.addCustomIssue')}
                      </button>
                    </div>
                    <div className="field-wrapper">
                      {customIssue ? (
                        <TextField
                          name="issue"
                          placeholder={t('maintenance:requestSummary.issue.placeholder')}
                          value={issue}
                          onChange={(e) => {
                            setIssue(e.target.value)
                            setRealPageIssue()
                            updateTextValue({ issue: e.target.value })
                          }}
                          disabled={isDisabled}
                          maxLength="255"
                        />
                      ) : (
                        <Filter
                          placeholder={
                            realPageIssue
                              ? realPageIssue.text
                              : t('maintenance:requestSummary.issue.selectIssue')
                          }
                          options={realPageIssuesToSelect}
                          value={realPageIssue ? [realPageIssue] : []}
                          name="Issue"
                          disabled={isDisabled}
                          config={{ hasSearch: true }}
                          closeAfterSelect
                          variant="rounded"
                          additionalClass={'details-filter'}
                          onChange={(e) => {
                            updateMaintenanceRequestValue({ issue: e.target.value.text })
                            setRealPageIssue(e.target.value)
                            setIssue(e.target.value.text)
                          }}
                        />
                      )}
                    </div>
                  </Fragment>
                ) : (
                  data.issue
                )}
              </p>
            </div>
          )}
          {entrataPropertyId && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.issue.title')}
              </h4>
              <p className="details-content-description">
                {isEditMode ? (
                  <div className="field-wrapper">
                    <TextField
                      name="issue"
                      placeholder={t('maintenance:requestSummary.issue.placeholder')}
                      value={issue}
                      onChange={(e) => {
                        setIssue(e.target.value)
                        updateTextValue({ issue: e.target.value })
                      }}
                      disabled={isDisabled}
                      maxLength="255"
                    />
                  </div>
                ) : (
                  data.issue
                )}
              </p>
            </div>
          )}
          {data.beforeRepairPhotos && data.beforeRepairPhotos.length ? (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestSummary.photosBeforeRepair')}
              </h4>
              <p className="details-content-description">{renderPhotos(data.beforeRepairPhotos)}</p>
            </div>
          ) : null}
          {data.jobSummaryPhotos && data.jobSummaryPhotos.length ? (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestPreview.jobSummary.photosAfterRepair')}
              </h4>
              <p className="details-content-description">{renderPhotos(data.jobSummaryPhotos)}</p>
            </div>
          ) : null}
          {status.text === 'Finished' && (
            <div className="details-content-section details-content-section-row">
              <h4 className="details-content-title">
                {t('maintenance:requestPreview.jobSummary.title')}
              </h4>
              <p className="details-content-description">
                <span>
                  {cutTooLong(jobSummaryMessage, STRING_LENGTH) ||
                    t('maintenance:requestPreview.jobSummary.noSummaryMessage')}
                  <button onClick={() => setShowJobSummaryModal(true)} className="text-button">
                    {t('maintenance:requestSummary.seeMore')}
                  </button>
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Fragment>
      <div
        className={`maintenance-request-container maintenance-request-details ${
          isLoading && 'is-loading'
        }`}
      >
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        <div className="single-row" style={{ margin: '0px 0px' }}>
          <div className="title-wrap">
            <h2>{t('maintenance:requestPreview.detailsBody.requestDetailsTitle')}</h2>
            <div className="hide-btn" onClick={() => setShowRequestDetails((prev) => !prev)}>
              {showRequestDetails
                ? t('maintenance:requestPreview.detailsBody.toggle.hide')
                : t('maintenance:requestPreview.detailsBody.toggle.show')}
              {showRequestDetails ? <CaretDown size={20} /> : <CaretUp size={20} />}
            </div>
          </div>
        </div>
        <div className={`details-content ${showRequestDetails ? '' : 'details-content-hidden'}`}>
          {renderRequestSummary()}
          <div className="details-content-divider" />
          <div className="details-content-block">
            <h4 className="details-content-title details-content-block-title">
              {t('maintenance:requestPreview.detailsBody.scheduling.title')}
            </h4>
            <div className="details-content-container">
              <div className="details-content-section details-content-section-row">
                <h4 className="details-content-title">
                  {t('maintenance:requestPreview.detailsBody.scheduling.scheduleDate.title')}
                </h4>
                <div className="details-content-description">
                  {isEditMode ? (
                    <div className="scheduled-date-time scheduled-date-time--compact">
                      <ScheduledDateField
                        value={scheduledDateTime.date}
                        timezone={timezone}
                        isCompact
                        disabled={isDisabled}
                        isError={scheduledDateValidity.isDateMissing}
                        onChange={(date) => handleScheduledDateTimeChange({ date })}
                      />
                    </div>
                  ) : data.scheduledDate ? (
                    formatInTimeZone(data.scheduledDate, timezone, 'MM/dd/yyyy')
                  ) : (
                    t('maintenance:requestPreview.detailsBody.scheduling.scheduleDate.fallback')
                  )}
                </div>
              </div>
              <div className="details-content-section details-content-section-row">
                <h4 className="details-content-title">
                  {t('maintenance:requestPreview.detailsBody.scheduling.scheduleTime.title')}
                </h4>
                <div className="details-content-description">
                  {isEditMode ? (
                    <div className="scheduled-date-time scheduled-date-time--compact">
                      <ScheduledTimeField
                        value={scheduledDateTime.time}
                        date={scheduledDateTime.date}
                        timezone={timezone}
                        isCompact
                        disabled={isDisabled}
                        isError={scheduledDateValidity.isTimeMissing}
                        onChange={(time) => handleScheduledDateTimeChange({ time })}
                      />
                    </div>
                  ) : data.scheduledDate ? (
                    formatInTimeZone(data.scheduledDate, timezone, 'h:mm a')
                  ) : (
                    t('maintenance:requestPreview.detailsBody.scheduling.scheduleTime.fallback')
                  )}
                </div>
              </div>
              <div className="details-content-section details-content-section-row">
                <h4 className="details-content-title">
                  {t('maintenance:requestPreview.detailsBody.scheduling.requestDate.title')}
                </h4>
                <p className="details-content-description">
                  {formatInTimeZone(data.createdAt, timezone, 'MM/dd/yyyy')}
                </p>
              </div>
              <div className="details-content-section details-content-section-row">
                <h4 className="details-content-title">
                  {t('maintenance:requestPreview.detailsBody.scheduling.suggestedTimeframe.title')}
                </h4>
                <p className="details-content-description">
                  {data.suggestedTimeframeAsap
                    ? t('maintenance:requestPreview.detailsBody.scheduling.suggestedTimeframe.asap')
                    : formatSuggestedTimeframe(
                        {
                          date: data.suggestedTimeframeDate,
                          end: data.suggestedTimeframeEnd,
                          start: data.suggestedTimeframeStart,
                        },
                        timezone,
                      ) || '-'}
                </p>
              </div>
              {(yardiPropertyId || rentManagerPropertyId) && (
                <div className="details-content-section details-content-section-row">
                  <h4 className="details-content-title">
                    {t('maintenance:requestPreview.detailsBody.scheduling.priority.title')}
                  </h4>
                  <p className="details-content-description">
                    {isEditMode ? (
                      <Filter
                        placeholder={
                          priority && priority.text
                            ? priority.text
                            : t(
                                'maintenance:requestPreview.detailsBody.scheduling.priority.placeholder',
                              )
                        }
                        options={prioritiesToSelect}
                        value={priority ? [priority] : []}
                        name="Priority"
                        closeAfterSelect
                        disabled={isDisabled}
                        variant="rounded"
                        additionalClass={'details-filter'}
                        onChange={(e) => {
                          updateMaintenanceRequestValue({ priority: e.target.value.text })
                          setPriority(e.target.value)
                        }}
                      />
                    ) : (
                      data.priority ||
                      t('maintenance:requestPreview.detailsBody.scheduling.priority.fallback')
                    )}
                  </p>
                </div>
              )}
              {staffList.length ? (
                <div className="details-content-section details-content-section-row">
                  <h4 className="details-content-title">
                    {t('maintenance:requestPreview.detailsBody.scheduling.assignee.title')}
                  </h4>
                  <div className="details-content-description assignTo-wrap">
                    <div className="input-column-container">
                      {isEditMode ? (
                        <Filter
                          placeholder={
                            assignee
                              ? assignee.text
                              : t(
                                  'maintenance:requestPreview.detailsBody.scheduling.assignee.placeholder',
                                )
                          }
                          options={staffList}
                          value={assignee ? [assignee] : []}
                          name="Assignee"
                          variant="rounded"
                          closeAfterSelect
                          disabled={isDisabled}
                          additionalClass={'details-filter'}
                          config={{ hasSearch: true }}
                          onChange={(e) => updateMaintenanceRequestAssignee(e.target.value)}
                        />
                      ) : data.assignee ? (
                        `${data.assignee.user.firstName} ${data.assignee.user.lastName}`
                      ) : (
                        t('maintenance:requestPreview.detailsBody.scheduling.assignee.fallback')
                      )}
                    </div>
                    {(isEditMode || data.assignee) && (
                      <button
                        onClick={() => setNotifyModalVisible(true)}
                        className="button button-gray"
                      >
                        <BellSimple size={20} />
                        {t('maintenance:requestPreview.detailsBody.scheduling.notifyAssignee')}
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className={'maintenance-request-container maintenance-request-details'}>
        <div className="single-row" style={{ margin: '0px 0px' }}>
          <div className="title-wrap">
            <h2>{t('maintenance:requestPreview.detailsBody.notesAndAttachments')}</h2>
            <div className="hide-btn" onClick={() => setShowNotes((prev) => !prev)}>
              {showNotes
                ? t('maintenance:requestPreview.detailsBody.toggle.hide')
                : t('maintenance:requestPreview.detailsBody.toggle.show')}
              {showNotes ? <CaretDown size={20} /> : <CaretUp size={20} />}
            </div>
          </div>
        </div>
        <div className={`details-content ${showNotes ? '' : 'details-content-hidden'}`}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <p className="details-content-description">
                {' '}
                {t('maintenance:requestPreview.detailsBody.notesLabel')}{' '}
                <span>({notes.length})</span>
              </p>
              <button onClick={() => setShowAddNoteModal(true)} className="text-button">
                {t('maintenance:requestPreview.detailsBody.notesAddButton')}
              </button>
            </div>
            {notes.length ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <p className="details-content-description">
                  {cutTooLong(notes[0].note, STRING_LENGTH)}
                </p>
                <button onClick={() => setShowNotesModal(true)} className="text-button">
                  {t('maintenance:requestSummary.seeMore')}
                </button>
              </div>
            ) : null}
          </div>
          <div className="details-content-divider" />
          <div style={{ width: '120px' }}>
            <div className="details-content-description">
              {t('maintenance:requestPreview.detailsBody.attachment')}
            </div>
            {data.photos && data.photos.length ? (
              <p className="details-content-description">
                <div
                  onClick={() => handleOpenModalWithPhotos(data.photos)}
                  className="resident-photo"
                >
                  <img
                    className="resident-photo__img"
                    src={getAssetLink({ path: 'chitown-building', file: data.photos[0] })}
                    alt=""
                  />
                </div>
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {photosToShow.length ? (
        <PicturesModal
          assetPath="chitown-building"
          visible={isPicturesModalVisible}
          pictures={photosToShow}
          onCloseModal={() => setPicturesModalVisible(false)}
        />
      ) : null}
      <NotifyModal
        id={data.id}
        isOpen={isNotifyModalVisible}
        handleClose={() => setNotifyModalVisible(false)}
      />
    </Fragment>
  )
}

MaintenanceRequestDetailsBody.propTypes = {
  data: PropTypes.object,
  setStatus: PropTypes.func,
  status: PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
    id: PropTypes.number,
  }),
  setShowNotesModal: PropTypes.func,
  notes: PropTypes.array,
  staffList: PropTypes.array,
  description: PropTypes.string,
  jobSummaryMessage: PropTypes.string,
  entryNote: PropTypes.string,
  setShowEntryNoteModal: PropTypes.func,
  setShowDescriptionModal: PropTypes.func,
  setShowJobSummaryModal: PropTypes.func,
  setShowCompleteRequestModal: PropTypes.func,
  getRecentActivityData: PropTypes.func,
  buildingData: PropTypes.object,
  entrataConfig: PropTypes.object,
  resmanConfig: PropTypes.object,
  yardiConfig: PropTypes.object,
  realPageConfig: PropTypes.object,
  rentManagerConfig: PropTypes.object,
  yardi: PropTypes.object,
  entrata: PropTypes.object,
  updateMaintenanceRequestValue: PropTypes.func,
  errorMessage: PropTypes.string,
  setShowAddNoteModal: PropTypes.func,
  isEditMode: PropTypes.bool,
  isLoading: PropTypes.bool,
}

export default MaintenanceRequestDetailsBody
